import { Typography } from "@mui/material"
import type { FC } from "react"
import { useShallow } from "zustand/react/shallow"

import { CountBox } from "./CountBox"

import { UOM_RENDER_PRECISION } from "@/constants/formats"
import type { SalesOrder, SalesOrderPricingTotalsRootTotal } from "@/graphql/codegen/graphql"
import { formatUomLabel } from "@/screens/Procurement/PurchaseOrder/utils/uoms"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatCurrency } from "@/utils/currency"
import { formatNumber } from "@/utils/number"

interface SalesOrderTotalsProps {
  salesOrder: SalesOrder
  pricingTotal: SalesOrderPricingTotalsRootTotal | null
  isPricingFetching?: boolean
}

export const SalesOrderTotals: FC<SalesOrderTotalsProps> = ({
  salesOrder,
  pricingTotal,
  isPricingFetching = false,
}) => {
  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))

  return (
    <div className='flex items-center justify-end gap-3' data-testid='customer-orders.so-line-items.totals-section'>
      <Typography variant='body2' className='mr-2 text-gray-700'>
        SO#: {salesOrder.publicId} Total
      </Typography>
      <CountBox
        label='Pallets'
        value={pricingTotal ? formatNumber(pricingTotal.pallets) : null}
        loading={isPricingFetching}
        data-testid='customer-orders.so-line-items.totals-pallets'
      />
      {pricingTotal?.weights.map(({ uomId, amount }) => (
        <CountBox
          key={uomId}
          label={formatUomLabel(units.find(({ id }) => id === uomId))}
          value={formatNumber(amount, UOM_RENDER_PRECISION)}
          loading={isPricingFetching}
          data-testid={`customer-orders.so-line-items.totals-weight-${uomId}`}
        />
      ))}
      <CountBox
        label='Units'
        value={pricingTotal ? formatNumber(pricingTotal.units) : null}
        loading={isPricingFetching}
        data-testid='customer-orders.so-line-items.totals-units'
      />
      <CountBox
        label='Amount'
        value={pricingTotal ? formatCurrency(pricingTotal.price) : null}
        loading={isPricingFetching}
        data-testid='customer-orders.so-line-items.totals-amount'
      />
    </div>
  )
}
